import moment from 'moment'

/**
 * How far back an appointment can be and still be treated as the patient's
 * current one. It matches the window the API already applies to the reschedule
 * rule — `validateReschedulePermission` only counts an appointment payment
 * approved within the last 6 months — so past this point the patient has to pay
 * again anyway and there is nothing to reschedule onto.
 */
const LIVE_APPOINTMENT_WINDOW_MONTHS = 6

/**
 * True when the patient's appointment is still the one they are actually on.
 *
 * `Appointment_Status` alone cannot answer this. Acuity only emits `scheduled`,
 * `rescheduled` and `canceled` — never "the date came and went" — and no job
 * closes the status out afterwards, so a patient who simply does not show up
 * keeps `Appointment_Status: "Scheduled"` and an `Appointment_Id` pointing at
 * that appointment forever. In production 35,733 patients carry a "Scheduled"
 * appointment whose date has already passed, against 151 whose date is still
 * ahead: on its own the flag is right about 0.4% of the time.
 *
 * Both places that used to read the flag alone need the date too. One decides
 * whether to reschedule the stored appointment instead of booking a new one,
 * the other reads that appointment's reason to pick which centers to offer, and
 * they have to agree — a flow that reschedules a four-year-old booking while
 * offering centers for a fresh one is worse than either answer.
 *
 * @param {Object} patient Patient record as returned by the middleware
 * @returns {Boolean} Whether the stored appointment can still be acted on
 */
const hasLiveAppointment = (patient) => {
  if (!patient || patient.Appointment_Status !== 'Scheduled') return false

  const appointmentDate = patient.Appointment_Date
  const iso = appointmentDate && (appointmentDate.iso || appointmentDate)
  if (!iso) return false

  const date = moment(iso)
  if (!date.isValid()) return false

  return date.isAfter(moment().subtract(LIVE_APPOINTMENT_WINDOW_MONTHS, 'months'))
}

export default hasLiveAppointment
